// src/pages/SuperAdminPanel.js — UPDATED with mall assignment
import SystemHealthTab from '../components/SystemHealthTab';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { AssignFormWithMall } from '../components/PricingHelpers';
import DeploymentModeManager  from '../components/DeploymentModeManager';
import FranchiseManager       from '../components/FranchiseManager';
import MallsTab               from './superadmin/MallsTab';
import SuperAdminReportsTab   from '../components/SuperAdminReportsTab';
import ListFilters from '../components/ListFilters';
import Pagination from '../components/Pagination';
import ConfirmModal from '../components/ConfirmModal';

const MODEL_TYPES = [
  { id:'COMMISSION_TIERED',   label:'Tiered Commission',        icon:'%' },
  { id:'PER_ORDER_TIERED',    label:'Per Order Tiered',         icon:'#' },
  { id:'FLAT_PLUS_PER_ORDER', label:'Monthly Flat + Per Order', icon:'₿' },
];

const DEFAULT_CONFIGS = {
  COMMISSION_TIERED:   { tiers:[{upTo:100,pct:2},{upTo:999999,pct:1}], minFee:2, maxFee:null },
  PER_ORDER_TIERED:    { tiers:[{upTo:200,fee:1},{upTo:400,fee:2},{upTo:600,fee:3},{upTo:800,fee:4},{upTo:1000,fee:5}], maxFee:10 },
  FLAT_PLUS_PER_ORDER: { monthlyFlat:2000, perOrderFee:1 },
};

export default function SuperAdminPanel() {
  const [tab,           setTab]           = useState('models');
  const [models,        setModels]        = useState([]);
  const [pricings,      setPricings]      = useState([]);
  const [vendors,       setVendors]       = useState([]);
  const [malls,         setMalls]         = useState([]);
  const [summary,       setSummary]       = useState([]);
  const [ledger,        setLedger]        = useState(null);
  const [ledgerError,   setLedgerError]   = useState(null);
  const [ledgerPage,    setLedgerPage]    = useState(1);
  const [ledgerDates,   setLedgerDates]   = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    return { startDate: weekAgo, endDate: today };
  });
  const [ledgerFilters, setLedgerFilters] = useState({ vendorId: '', mallId: '', recipient: '' });
  const [platformRev,   setPlatformRev]   = useState(null);
  const [paymentHealth, setPaymentHealth] = useState(null);
  const [accounts,      setAccounts]      = useState(null);
  const [resetTarget,   setResetTarget]   = useState(null); // { id, type, name, email }
  const [resetForm,     setResetForm]     = useState({ newEmail:'', newPassword:'' });
  const [resetMsg,      setResetMsg]      = useState('');
  const [editModel,     setEditModel]     = useState(null);
  const [newModel,      setNewModel]      = useState(false);
  const [assignForm,    setAssignForm]    = useState(null);
  const [previewAOV,    setPreviewAOV]    = useState(85);
  const [msg,           setMsg]           = useState('');
  const [loading,       setLoading]       = useState(false);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const loadModels  = useCallback(async () => { try { const r = await api.get('/super-admin/pricing-models'); setModels(r.data||[]); } catch(e) { showMsg('❌ Models: '+(e.response?.data?.error||e.message)); } }, []);
  const loadPricings= useCallback(async () => { try { const r = await api.get('/super-admin/vendor-pricing'); setPricings(r.data||[]); } catch(_) {} }, []);
  const loadVendors = useCallback(async () => { try { const r = await api.get('/vendors'); setVendors(r.data||[]); } catch(_) {} }, []);
  const loadMalls   = useCallback(async () => { try { const r = await api.get('/super-admin/malls'); setMalls(r.data||[]); } catch(_) {} }, []);
  const loadSummary = useCallback(async () => { try { const r = await api.get('/super-admin/vendors-summary'); setSummary(r.data||[]); } catch(_) {} }, []);
  const loadLedger = useCallback(async () => {
    setLedgerError(null);
    try {
      const params = {
        page: ledgerPage, limit: 100,
        startDate: ledgerDates.startDate,
        endDate:   ledgerDates.endDate,
        maxRange:  7,
        ...(ledgerFilters.vendorId  && { vendorId:  ledgerFilters.vendorId }),
        ...(ledgerFilters.mallId    && { mallId:    ledgerFilters.mallId }),
        ...(ledgerFilters.recipient && { recipient: ledgerFilters.recipient }),
      };
      const [ledgerRes, revRes] = await Promise.allSettled([
        api.get('/super-admin/ledger', { params }),
        api.get('/super-admin/platform-revenue'),
      ]);
      if (ledgerRes.status === 'fulfilled') setLedger(ledgerRes.value.data);
      else setLedgerError(ledgerRes.reason?.response?.data?.error || ledgerRes.reason?.message || 'Failed to load ledger');
      if (revRes.status === 'fulfilled') setPlatformRev(revRes.value.data);
    } catch (e) { setLedgerError(e.response?.data?.error || e.message || 'Unknown error'); }
  }, [ledgerPage, ledgerDates, ledgerFilters]);
  const loadPaymentHealth = useCallback(async () => { try { const r = await api.get('/super-admin/payment-health'); setPaymentHealth(r.data); } catch(_) {} }, []);
  const loadAccounts      = useCallback(async () => { try { const r = await api.get('/super-admin/accounts'); setAccounts(r.data); } catch(_) {} }, []);

  useEffect(() => { loadModels(); loadVendors(); loadMalls(); }, [loadModels, loadVendors, loadMalls]);
  useEffect(() => {
    if (tab==='assign')    { loadPricings(); }
    if (tab==='summary')   { loadSummary(); }
    if (tab==='ledger')    { loadLedger(); }
    if (tab==='payhealth') { loadPaymentHealth(); }
    if (tab==='accounts')  { loadAccounts(); }
  }, [tab, loadPricings, loadSummary, loadLedger, loadPaymentHealth, loadAccounts]);

  // Reload ledger whenever page or filters change
  useEffect(() => {
    if (tab === 'ledger') loadLedger();
  }, [ledgerPage, ledgerDates, ledgerFilters]); // eslint-disable-line

  const handleSuperAdminRefund = async (orderId) => {
    if (!window.confirm('Issue refund for this order? The payment will be returned to the customer.')) return;
    try {
      await api.post('/payments/admin/refund', { orderId });
      showMsg('✅ Order refunded');
      loadLedger();
    } catch (err) {
      alert(err.response?.data?.error || 'Refund failed');
    }
  };

  const handleSeedModels = async () => {
    try { const r = await api.post('/super-admin/seed-models'); showMsg('✅ '+(r.data.message||'Seeded')); loadModels(); }
    catch(e) { showMsg('❌ '+(e.response?.data?.error||e.message)); }
  };

  const handleSaveModel = async (formData) => {
    setLoading(true);
    try {
      if (editModel?.id) { await api.patch(`/super-admin/pricing-models/${editModel.id}`, formData); showMsg('✅ Model updated'); }
      else               { await api.post('/super-admin/pricing-models', formData); showMsg('✅ Model created'); }
      setEditModel(null); setNewModel(false); loadModels();
    } catch(e) { showMsg('❌ Save failed: '+(e.response?.data?.error||e.message)); }
    finally { setLoading(false); }
  };

  const handleToggleModel = async (model) => {
    try {
      if (model.isActive) await api.delete(`/super-admin/pricing-models/${model.id}`);
      else                await api.patch(`/super-admin/pricing-models/${model.id}`, { isActive: true });
      showMsg(model.isActive ? '✅ Model deactivated' : '✅ Model reactivated');
      loadModels();
    } catch(e) { showMsg('❌ '+(e.response?.data?.error||e.message)); }
  };

  const handleAssign = async (formData) => {
    setLoading(true);
    try { await api.post('/super-admin/vendor-pricing', formData); showMsg('✅ Pricing assigned'); setAssignForm(null); loadPricings(); }
    catch(e) { showMsg('❌ '+(e.response?.data?.error||e.message)); }
    finally { setLoading(false); }
  };

  const tabStyle = (id) => ({
    padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:500, border:'none', cursor:'pointer',
    background: tab===id?'var(--accent)':'transparent', color: tab===id?'#000':'var(--text2)',
  });

  return (
    <div className="page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <div className="page-title">⚡ Super Admin — Pricing Engine</div>
          <div className="page-sub">Manage pricing models · assign to vendors or malls · view commission ledger</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={handleSeedModels}>🌱 Seed Default Models</button>
      </div>

      {msg && (
        <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13,
          background:msg.startsWith('✅')?'#1a3d2a':'#3d1a1a', color:msg.startsWith('✅')?'var(--green)':'var(--red)',
          border:`1px solid ${msg.startsWith('✅')?'var(--green)':'var(--red)'}` }}>
          {msg}
        </div>
      )}

      <div style={{ display:'flex', gap:4, marginBottom:24, background:'var(--bg2)', borderRadius:10, padding:4, width:'fit-content', flexWrap:'wrap' }}>
        {[['models','📐 Pricing Models'],['assign','🔗 Assignments'],['summary','📊 Summary'],['ledger','📒 Ledger'],['payhealth','💳 Payment Health'],['accounts','👤 Accounts'],['preview','🧮 Calculator'],['deployment','🏗 Deployment'],['chains','⛓ Chains'],['malls','🏬 Malls'],['reports','📊 Reports'],['syshealth','System Health']].map(([id,label]) => (
          <button key={id} style={tabStyle(id)} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── MODELS ── */}
      {tab === 'models' && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ fontSize:13, color:'var(--text2)' }}>{models.length} model{models.length!==1?'s':''}</div>
            <button className="btn btn-primary" onClick={() => { setNewModel(true); setEditModel(null); }}>+ New Model</button>
          </div>
          {(newModel||editModel) && <ModelForm initial={editModel||null} onSave={handleSaveModel} onCancel={()=>{setNewModel(false);setEditModel(null);}} loading={loading} />}
          {models.length===0 && <div className="card" style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>No models. Click 🌱 Seed Default Models.</div>}
          {models.map(model => (
            <div key={model.id} className="card" style={{ marginBottom:14, padding:0, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', background:'var(--bg3)', borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:'var(--bg4)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--accent)', fontSize:18 }}>
                    {MODEL_TYPES.find(t=>t.id===model.type)?.icon||'%'}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                      {model.name}
                      {model.isDefault && <span className="badge badge-green">Default</span>}
                      {!model.isActive && <span className="badge badge-red">Inactive</span>}
                    </div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>{model.description}</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditModel(model); setNewModel(false); }}>Edit</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setAssignForm({ modelId:model.id }); setTab('assign'); }}>Assign →</button>
                  <button className={`btn btn-sm ${model.isActive?'btn-danger':'btn-primary'}`} onClick={() => handleToggleModel(model)}>{model.isActive?'Deactivate':'Reactivate'}</button>
                </div>
              </div>
              <div style={{ padding:'12px 18px' }}><ModelConfigDisplay type={model.type} config={model.config} /></div>
            </div>
          ))}
        </>
      )}

      {/* ── ASSIGN ── */}
      {tab === 'assign' && (
        <>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
            <button className="btn btn-primary" onClick={() => setAssignForm({})}>+ Assign Model</button>
          </div>
          {assignForm !== null && (
            <AssignFormWithMall models={models} vendors={vendors} malls={malls} initial={assignForm}
              onSave={handleAssign} onCancel={() => setAssignForm(null)} loading={loading} />
          )}
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
              Pricing Assignments ({pricings.length})
            </div>
            {pricings.length===0 && <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>No assignments yet.</div>}
            {pricings.map(p => (
              <div key={p.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'13px 16px', borderBottom:'1px solid var(--border)', opacity:p.isActive?1:.5 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:22 }}>{p.vendor ? (p.vendor.emoji||'🍽') : '🏬'}</span>
                  <div>
                    <div style={{ fontWeight:600 }}>{p.vendor ? p.vendor.name : `Mall: ${p.mallId?.slice(0,8)}...`}</div>
                    <div style={{ fontSize:12, color:'var(--text2)' }}>
                      {p.vendor ? p.vendor.email : 'Mall-wide assignment'}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{p.pricingModel?.name}</div>
                  <div style={{ fontSize:11, color:'var(--text2)' }}>{p.pricingModel?.type?.replace(/_/g,' ')}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{new Date(p.effectiveFrom).toLocaleDateString()}</div>
                  {p.agreedBy && <div style={{ fontSize:11, color:'var(--text3)' }}>By: {p.agreedBy}</div>}
                  {p.notes    && <div style={{ fontSize:11, color:'var(--text3)' }}>{p.notes}</div>}
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <span className={`badge ${p.isActive?'badge-green':'badge-red'}`}>{p.isActive?'Active':'Inactive'}</span>
                  {p.customConfig && <span className="badge badge-amber">Custom</span>}
                  {!p.vendorId    && <span className="badge badge-blue">Mall-wide</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── SUMMARY ── */}
      {tab === 'summary' && (
        <>
          <div className="grid4" style={{ marginBottom:20 }}>
            {[['Vendors',summary.length],['Total GMV',`AED ${summary.reduce((s,v)=>s+v.totalGMV,0).toLocaleString()}`],['Total Fees',`AED ${summary.reduce((s,v)=>s+v.totalFees,0).toLocaleString()}`],['Total Net',`AED ${summary.reduce((s,v)=>s+v.totalNet,0).toLocaleString()}`]].map(([l,v])=>(
              <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-val" style={{ fontSize:18 }}>{v}</div></div>
            ))}
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>Vendor Summary</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr>{['Vendor','Model','Orders','GMV','Fees','Net'].map(h=><th key={h} style={{ textAlign:'left', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
              <tbody>
                {summary.map(v=>(
                  <tr key={v.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'11px 14px', fontWeight:600 }}>{v.emoji} {v.name}</td>
                    <td style={{ padding:'11px 14px' }}><div style={{ fontSize:12 }}>{v.pricing?.modelName}</div>{v.pricing?.isDefault&&<span className="badge badge-amber" style={{ fontSize:10 }}>default</span>}</td>
                    <td style={{ padding:'11px 14px', color:'var(--text2)' }}>{v.totalOrders}</td>
                    <td style={{ padding:'11px 14px' }}>AED {v.totalGMV.toLocaleString()}</td>
                    <td style={{ padding:'11px 14px', color:'var(--red)' }}>AED {v.totalFees.toLocaleString()}</td>
                    <td style={{ padding:'11px 14px', fontWeight:700, color:'var(--green)' }}>AED {v.totalNet.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── LEDGER ── */}
      {tab === 'ledger' && (
        <>
          {ledgerError && (
            <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'#3d1a1a', color:'var(--red)', fontSize:13 }}>
              ❌ {ledgerError} — check that you are logged in as super admin.
              <button className="btn btn-sm btn-ghost" style={{ marginLeft:12 }} onClick={loadLedger}>Retry</button>
            </div>
          )}

          {/* Platform Revenue Summary */}
          {platformRev && (
            <div className="card" style={{ marginBottom:20 }}>
              <div style={{ fontWeight:700, marginBottom:14 }}>📊 Platform Revenue (last {platformRev.days} days)</div>
              <div className="grid4" style={{ marginBottom:16 }}>
                {[
                  ['Commission Earned', `AED ${Number(platformRev.totalCommission).toLocaleString('en',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
                  ['Service Fee Revenue', `AED ${Number(platformRev.totalServiceFees).toLocaleString('en',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
                  ['Total Platform Revenue', `AED ${Number(platformRev.totalRevenue).toLocaleString('en',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
                  ['Total GMV', `AED ${Number(platformRev.totalGMV).toLocaleString('en',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
                ].map(([l,v])=>(
                  <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-val" style={{ fontSize:15 }}>{v}</div></div>
                ))}
              </div>
              {(platformRev.perMall||[]).length > 0 && (
                <>
                  <div style={{ fontWeight:600, marginBottom:10, fontSize:13 }}>Per-Mall Breakdown</div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead><tr>{['Mall','Commission','Service Fees','Total','Pending Payouts'].map(h=><th key={h} style={{ textAlign:'left', padding:'8px 12px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {platformRev.perMall.map(m=>(
                        <tr key={m.mallId} style={{ borderBottom:'1px solid var(--border)' }}>
                          <td style={{ padding:'9px 12px', fontWeight:600 }}>{m.mallName}</td>
                          <td style={{ padding:'9px 12px' }}>AED {Number(m.commission).toFixed(2)}</td>
                          <td style={{ padding:'9px 12px' }}>AED {Number(m.platformFees).toFixed(2)}</td>
                          <td style={{ padding:'9px 12px', fontWeight:700, color:'var(--green)' }}>AED {Number(m.total).toFixed(2)}</td>
                          <td style={{ padding:'9px 12px' }}>{m.pendingPayouts > 0 ? <span className="badge badge-red">{m.pendingPayouts} pending</span> : <span style={{ color:'var(--text3)' }}>—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          <ListFilters
            startDate={ledgerDates.startDate}
            endDate={ledgerDates.endDate}
            maxRangeDays={7}
            onDateChange={({ startDate, endDate }) => { setLedgerDates({ startDate, endDate }); setLedgerPage(1); }}
            filters={[
              {
                key: 'vendorId', label: 'Vendor', value: ledgerFilters.vendorId,
                options: ['', ...vendors.map(v => v.id)].filter((v,i,a)=>a.indexOf(v)===i),
                onChange: v => { setLedgerFilters(f => ({ ...f, vendorId: v })); setLedgerPage(1); },
              },
              {
                key: 'mallId', label: 'Mall', value: ledgerFilters.mallId,
                options: ['', ...malls.map(m => m.id)].filter((v,i,a)=>a.indexOf(v)===i),
                onChange: v => { setLedgerFilters(f => ({ ...f, mallId: v })); setLedgerPage(1); },
              },
              {
                key: 'recipient', label: 'Recipient', value: ledgerFilters.recipient,
                options: ['', 'PLATFORM', 'MALL'],
                onChange: v => { setLedgerFilters(f => ({ ...f, recipient: v })); setLedgerPage(1); },
              },
            ]}
          />

          {ledger && (
            <>
              <div className="grid4" style={{ marginBottom:20 }}>
                {[
                  ['Total Transactions', ledger.totals.count],
                  ['Total GMV', `AED ${Number(ledger.totals.totalGMV).toLocaleString('en',{minimumFractionDigits:2})}`],
                  ['Fees Charged', `AED ${Number(ledger.totals.totalFees).toLocaleString('en',{minimumFractionDigits:2})}`],
                  ['Net to Vendors', `AED ${Number(ledger.totals.totalNet).toLocaleString('en',{minimumFractionDigits:2})}`],
                ].map(([l,v])=>(
                  <div key={l} className="stat-card"><div className="stat-label">{l}</div><div className="stat-val" style={{ fontSize:16 }}>{v}</div></div>
                ))}
              </div>
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
                  Commission Ledger ({ledger.totals.count} entries)
                </div>
                {(ledger.data||[]).length===0
                  ? <div style={{ textAlign:'center', padding:40, color:'var(--text3)' }}>No records found for this period.</div>
                  : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                      <thead><tr>{['Order #','Model','Order Value','Fee','Net','Breakdown','Date','Payment','Action'].map(h=><th key={h} style={{ textAlign:'left', padding:'9px 12px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {(ledger.data||[]).map(e=>(
                          <tr key={e.id} style={{ borderBottom:'1px solid var(--border)' }}>
                            <td style={{ padding:'9px 12px', fontWeight:600 }}>{e.orderNumber || '—'}</td>
                            <td style={{ padding:'9px 12px', color:'var(--text2)' }}>{e.modelType?.replace(/_/g,' ')}</td>
                            <td style={{ padding:'9px 12px' }}>AED {Number(e.orderValue).toFixed(2)}</td>
                            <td style={{ padding:'9px 12px', color:'var(--red)' }}>AED {Number(e.feeCharged).toFixed(2)}</td>
                            <td style={{ padding:'9px 12px', fontWeight:600, color:'var(--green)' }}>AED {Number(e.netAmount).toFixed(2)}</td>
                            <td style={{ padding:'9px 12px', color:'var(--text3)', fontSize:11, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {typeof e.calculation==='object'?e.calculation?.breakdown:e.calculation}
                            </td>
                            <td style={{ padding:'9px 12px', color:'var(--text2)', whiteSpace:'nowrap' }}>
                              {new Date(e.createdAt).toLocaleString('en',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                            </td>
                            <td style={{ padding:'9px 12px' }}>
                              {e.paymentStatus && (
                                <span className={`badge ${e.paymentStatus==='PAID'?'badge-green':e.paymentStatus==='REFUNDED'||e.paymentStatus==='FAILED'?'badge-red':'badge-amber'}`}>{e.paymentStatus}</span>
                              )}
                            </td>
                            <td style={{ padding:'9px 12px' }}>
                              {e.paymentStatus === 'PAID' && (
                                <button
                                  className="btn btn-danger btn-sm"
                                  style={{ fontSize:11 }}
                                  onClick={() => handleSuperAdminRefund(e.orderId)}>
                                  Refund
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                }
              </div>
              <Pagination
                page={ledger.pagination?.page || ledgerPage}
                totalPages={ledger.pagination?.totalPages || 0}
                total={ledger.pagination?.total || 0}
                limit={ledger.pagination?.limit || 100}
                onPageChange={p => setLedgerPage(p)}
              />
            </>
          )}

          {!ledger && !ledgerError && (
            <div style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>Loading ledger…</div>
          )}
        </>
      )}

      {/* ── CALCULATOR ── */}
      {tab === 'preview' && (
        <div className="card">
          <div style={{ fontWeight:700, marginBottom:16 }}>🧮 Live Fee Calculator</div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <div className="section-label" style={{ marginBottom:0, whiteSpace:'nowrap' }}>Order value (AED)</div>
            <input type="number" value={previewAOV} min="1" max="5000" step="5" onChange={e=>setPreviewAOV(Number(e.target.value))} style={{ width:120 }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
            {models.map(model => {
              const f = calcFee(previewAOV, model.type, model.config);
              return (
                <div key={model.id} style={{ background:'var(--bg3)', borderRadius:10, padding:16, border:'1px solid var(--border)' }}>
                  <div style={{ fontWeight:600, marginBottom:4 }}>{model.name}</div>
                  <div style={{ fontSize:11, color:'var(--text2)', marginBottom:10 }}>{model.description}</div>
                  <div style={{ fontSize:26, fontWeight:700, color:'var(--accent)', marginBottom:4 }}>AED {f.fee.toFixed(2)}</div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>{previewAOV>0?((f.fee/previewAOV)*100).toFixed(1):0}% of order</div>
                  <div style={{ fontSize:11, color:'var(--green)', marginTop:4 }}>Vendor gets: AED {(previewAOV-f.fee).toFixed(2)}</div>
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:8, lineHeight:1.6 }}>{f.breakdown}</div>
                </div>
              );
            })}
          </div>
          {models.length>1&&previewAOV>0&&(
            <div style={{ marginTop:16, padding:'12px 16px', background:'var(--bg3)', borderRadius:8, fontSize:13 }}>
              <span style={{ fontWeight:600 }}>Lowest fee: </span>
              {(() => { const fees=models.map(m=>({name:m.name,fee:calcFee(previewAOV,m.type,m.config).fee})); const mn=fees.reduce((a,b)=>a.fee<b.fee?a:b); return <span style={{ color:'var(--green)' }}>{mn.name} — AED {mn.fee.toFixed(2)}</span>; })()}
            </div>
          )}
        </div>
      )}

      {tab === 'deployment' && <DeploymentModeManager />}
      {tab === 'chains'     && <FranchiseManager />}
      {tab === 'malls'      && <MallsTab />}
      {tab === 'reports'    && <SuperAdminReportsTab />}

      {tab === 'syshealth' && (
        <div style={{ padding:'4px 0' }}>
          <SystemHealthTab />
        </div>
      )}
      {tab === 'payhealth' && (
        <PlatformPaymentHealth stats={paymentHealth} onRefresh={loadPaymentHealth} />
      )}

      {tab === 'accounts' && (
        <AccountsTab
          accounts={accounts}
          onRefresh={loadAccounts}
          resetTarget={resetTarget}
          setResetTarget={setResetTarget}
          resetForm={resetForm}
          setResetForm={setResetForm}
          resetMsg={resetMsg}
          setResetMsg={setResetMsg}
          showMsg={showMsg}
        />
      )}

    </div>
  );
}

function calcFee(orderValue, modelType, config) {
  if (!orderValue||!config) return { fee:0, breakdown:'No config' };
  if (modelType==='COMMISSION_TIERED') {
    const tiers=config.tiers||[]; let fee=0,remaining=orderValue,prev=0; const steps=[];
    for (const tier of tiers) {
      if (remaining<=0) break;
      const upTo=tier.upTo>=999999?Infinity:tier.upTo;
      const chunk=upTo===Infinity?remaining:Math.min(tier.upTo-prev,remaining);
      const tf=parseFloat((chunk*(tier.pct/100)).toFixed(4));
      steps.push(`AED ${chunk.toFixed(0)} @ ${tier.pct}%`);
      fee+=tf; remaining-=chunk; prev=upTo===Infinity?Infinity:tier.upTo;
    }
    const raw=parseFloat(fee.toFixed(2)); const final=Math.max(raw,config.minFee||0);
    return { fee:final, breakdown:steps.join(' + ')+(final>raw?` (min AED ${config.minFee})`:'') };
  }
  if (modelType==='PER_ORDER_TIERED') {
    const tiers=config.tiers||[]; const maxFee=config.maxFee||10; let fee=maxFee;
    for (const tier of tiers) { if (orderValue<=tier.upTo) { fee=tier.fee; break; } }
    return { fee:Math.min(fee,maxFee), breakdown:`Flat fee (max AED ${maxFee})` };
  }
  if (modelType==='FLAT_PLUS_PER_ORDER') {
    return { fee:config.perOrderFee||1, breakdown:`AED ${config.monthlyFlat}/mo + AED ${config.perOrderFee}/order` };
  }
  return { fee:0, breakdown:'Unknown' };
}

function ModelConfigDisplay({ type, config }) {
  if (!config) return null;
  if (type==='COMMISSION_TIERED') return <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>{(config.tiers||[]).map((t,i)=><span key={i} className="tag">{t.upTo>=999999?'Above prev':`≤ AED ${t.upTo}`}: {t.pct}%</span>)}{config.minFee!=null&&<span className="tag" style={{ color:'var(--accent)' }}>Min: AED {config.minFee}</span>}</div>;
  if (type==='PER_ORDER_TIERED')  return <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>{(config.tiers||[]).map((t,i)=><span key={i} className="tag">≤ AED {t.upTo} → AED {t.fee}</span>)}<span className="tag" style={{ color:'var(--accent)' }}>Max: AED {config.maxFee}</span></div>;
  if (type==='FLAT_PLUS_PER_ORDER') return <div style={{ display:'flex', gap:8 }}><span className="tag">AED {config.monthlyFlat}/month</span><span className="tag">+ AED {config.perOrderFee}/order</span></div>;
  return null;
}

function ModelForm({ initial, onSave, onCancel, loading }) {
  const [name,      setName]      = useState(initial?.name||'');
  const [type,      setType]      = useState(initial?.type||'COMMISSION_TIERED');
  const [desc,      setDesc]      = useState(initial?.description||'');
  const [isDefault, setIsDefault] = useState(initial?.isDefault||false);
  const [config,    setConfig]    = useState(initial?.config||DEFAULT_CONFIGS['COMMISSION_TIERED']);
  useEffect(() => { if (!initial) setConfig({...DEFAULT_CONFIGS[type]}); }, [type, initial]);

  return (
    <div className="card" style={{ marginBottom:16, border:'1px solid var(--accent)' }}>
      <div style={{ fontWeight:700, marginBottom:16 }}>{initial?`Edit: ${initial.name}`:'New Pricing Model'}</div>
      <div className="grid2" style={{ gap:12, marginBottom:12 }}>
        <div><div className="section-label" style={{ marginBottom:6 }}>Model Name *</div><input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Standard Commission" /></div>
        <div><div className="section-label" style={{ marginBottom:6 }}>Model Type</div><select value={type} onChange={e=>setType(e.target.value)}>{MODEL_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}</select></div>
        <div><div className="section-label" style={{ marginBottom:6 }}>Description</div><input type="text" value={desc} onChange={e=>setDesc(e.target.value)} /></div>
        <div style={{ display:'flex', alignItems:'center', gap:10, paddingTop:22 }}>
          <div className={`toggle${isDefault?' on':''}`} onClick={()=>setIsDefault(p=>!p)} style={{ cursor:'pointer' }} />
          <span style={{ fontSize:13, color:'var(--text2)' }}>Set as default model</span>
        </div>
      </div>
      <div style={{ marginBottom:16 }}><div className="section-label" style={{ marginBottom:10 }}>Configuration</div><ConfigEditor type={type} config={config} onChange={setConfig} /></div>
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary" disabled={loading||!name} onClick={()=>onSave({name,type,description:desc,isDefault,config})}>{loading?'⏳ Saving...':'Save Model'}</button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ConfigEditor({ type, config, onChange }) {
  const update = (key, val) => onChange({ ...config, [key]: val });
  if (type==='COMMISSION_TIERED') return (
    <div>
      {(config.tiers||[]).map((tier,i)=>(
        <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:'var(--text2)', minWidth:50 }}>Tier {i+1}</span>
          <span style={{ fontSize:13, color:'var(--text2)' }}>Up to AED</span>
          <input type="number" value={tier.upTo>=999999?'':tier.upTo} placeholder="∞" style={{ width:80 }}
            onChange={e=>{const t=[...config.tiers];t[i]={...t[i],upTo:e.target.value?Number(e.target.value):999999};update('tiers',t);}} />
          <span style={{ fontSize:13, color:'var(--text2)' }}>at</span>
          <input type="number" value={tier.pct} step="0.5" style={{ width:70 }}
            onChange={e=>{const t=[...config.tiers];t[i]={...t[i],pct:Number(e.target.value)};update('tiers',t);}} />
          <span style={{ fontSize:13, color:'var(--text2)' }}>%</span>
          {config.tiers.length>1&&<button onClick={()=>update('tiers',config.tiers.filter((_,j)=>j!==i))} style={{ background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:16 }}>✕</button>}
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" onClick={()=>update('tiers',[...config.tiers,{upTo:999999,pct:1}])}>+ Add Tier</button>
      <div style={{ display:'flex', gap:12, marginTop:12 }}>
        <div><div className="section-label" style={{ marginBottom:6 }}>Min fee (AED)</div><input type="number" value={config.minFee||0} style={{ width:80 }} onChange={e=>update('minFee',Number(e.target.value))} /></div>
        <div><div className="section-label" style={{ marginBottom:6 }}>Max fee (AED)</div><input type="number" value={config.maxFee||''} style={{ width:80 }} placeholder="None" onChange={e=>update('maxFee',e.target.value?Number(e.target.value):null)} /></div>
      </div>
    </div>
  );
  if (type==='PER_ORDER_TIERED') return (
    <div>
      {(config.tiers||[]).map((tier,i)=>(
        <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
          <span style={{ fontSize:13, color:'var(--text2)', minWidth:50 }}>Tier {i+1}</span>
          <span style={{ fontSize:13, color:'var(--text2)' }}>Order ≤ AED</span>
          <input type="number" value={tier.upTo} style={{ width:80 }} onChange={e=>{const t=[...config.tiers];t[i]={...t[i],upTo:Number(e.target.value)};update('tiers',t);}} />
          <span style={{ fontSize:13, color:'var(--text2)' }}>→ AED</span>
          <input type="number" value={tier.fee} style={{ width:60 }} onChange={e=>{const t=[...config.tiers];t[i]={...t[i],fee:Number(e.target.value)};update('tiers',t);}} />
          {config.tiers.length>1&&<button onClick={()=>update('tiers',config.tiers.filter((_,j)=>j!==i))} style={{ background:'none',border:'none',color:'var(--red)',cursor:'pointer',fontSize:16 }}>✕</button>}
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" onClick={()=>update('tiers',[...config.tiers,{upTo:(config.tiers[config.tiers.length-1]?.upTo||0)+200,fee:config.tiers.length+1}])}>+ Add Tier</button>
      <div style={{ marginTop:12 }}><div className="section-label" style={{ marginBottom:6 }}>Max fee cap (AED)</div><input type="number" value={config.maxFee||10} style={{ width:80 }} onChange={e=>update('maxFee',Number(e.target.value))} /></div>
    </div>
  );
  if (type==='FLAT_PLUS_PER_ORDER') return (
    <div style={{ display:'flex', gap:16 }}>
      <div><div className="section-label" style={{ marginBottom:6 }}>Monthly flat (AED)</div><input type="number" value={config.monthlyFlat||2000} style={{ width:120 }} onChange={e=>update('monthlyFlat',Number(e.target.value))} /></div>
      <div><div className="section-label" style={{ marginBottom:6 }}>Per order (AED)</div><input type="number" value={config.perOrderFee||1} style={{ width:100 }} step="0.5" onChange={e=>update('perOrderFee',Number(e.target.value))} /></div>
    </div>
  );
  return null;
}

// ── Platform Payment Health ───────────────────────────────────
function PlatformPaymentHealth({ stats, onRefresh }) {
  if (!stats) return (
    <div className="card" style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>💳</div>
      <div style={{ marginBottom:16 }}>No payment health data loaded</div>
      <button className="btn btn-primary" onClick={onRefresh}>Load Platform Health</button>
    </div>
  );

  const srColor = stats.successRate >= 95 ? 'var(--green)' : stats.successRate >= 85 ? 'var(--accent)' : 'var(--red)';
  const deployCols = Object.entries(stats.byDeployment || {});
  const malls      = Object.entries(stats.revenueByMall || {}).sort((a,b) => b[1]-a[1]);

  return (
    <div>
      <div className="grid4" style={{ marginBottom:20 }}>
        {[
          ['Platform Success Rate', `${stats.successRate}%`,                           srColor],
          ['Total Attempts',        stats.totalAttempts,                               'var(--text)'],
          ['Platform Revenue',      `AED ${Number(stats.totalRevenue||0).toFixed(0)}`, 'var(--green)'],
          ['Failed',                stats.totalFailed,                                 stats.totalFailed > 0 ? 'var(--red)' : 'var(--text2)'],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-card">
            <div className="stat-label">{l}</div>
            <div className="stat-val" style={{ color:c, fontSize:20 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid2" style={{ marginBottom:20 }}>
        <div className="card">
          <div className="section-label" style={{ marginBottom:14 }}>📡 Success Rate by Deployment</div>
          {deployCols.map(([mode, d]) => {
            const color = d.successRate >= 95 ? 'var(--green)' : d.successRate >= 85 ? 'var(--accent)' : 'var(--red)';
            return (
              <div key={mode} style={{ marginBottom:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:13 }}>
                  <span style={{ fontWeight:600 }}>{mode}</span>
                  <span style={{ color, fontWeight:700 }}>{d.successRate}% ({d.total} attempts)</span>
                </div>
                <div style={{ background:'var(--bg3)', borderRadius:4, height:8 }}>
                  <div style={{ width:`${d.successRate}%`, background:color, height:8, borderRadius:4, transition:'width .4s' }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="section-label" style={{ marginBottom:12 }}>🚨 Anomaly Detection (last 24h)</div>
          {(stats.anomalies || []).length === 0
            ? <div style={{ color:'var(--green)', fontSize:13 }}>✅ No anomalies detected</div>
            : (stats.anomalies || []).map(({ vendorId, failuresLast24h }) => (
              <div key={vendorId} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                <div style={{ fontFamily:'monospace', fontSize:12, color:'var(--text2)' }}>{vendorId.slice(0,16)}…</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ color:'var(--red)', fontWeight:700 }}>{failuresLast24h} failures</span>
                  <span className="badge badge-red">ALERT</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="section-label" style={{ marginBottom:12 }}>❌ Top Failure Reasons — Platform-wide</div>
        {(stats.failureReasons || []).length === 0
          ? <div style={{ color:'var(--text3)', fontSize:13 }}>No failures recorded</div>
          : (stats.failureReasons || []).map(({ reason, count }, i) => (
            <div key={reason} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
              <div style={{ width:22, textAlign:'center', fontWeight:700, color:'var(--text3)', fontSize:13 }}>#{i+1}</div>
              <div style={{ flex:1, background:'var(--bg3)', borderRadius:4, height:24, display:'flex', alignItems:'center', overflow:'hidden' }}>
                <div style={{ width:`${Math.min(100, (count / (stats.failureReasons[0]?.count||1)) * 100)}%`, background:'var(--red)', height:'100%', minWidth:4 }} />
              </div>
              <div style={{ fontSize:13, color:'var(--text2)', minWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{reason}</div>
              <span className="badge badge-red">{count}</span>
            </div>
          ))
        }
      </div>

      {malls.length > 0 && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border)', fontWeight:700 }}>
            🏬 Revenue by Mall (commission base)
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>{['Mall ID','Total Revenue (AED)','Share %'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {malls.map(([mallId, rev]) => (
                <tr key={mallId}>
                  <td style={{ padding:'11px 14px', fontFamily:'monospace', fontSize:12 }}>{mallId === '__standalone__' ? 'Standalone vendors' : mallId}</td>
                  <td style={{ padding:'11px 14px', color:'var(--green)', fontWeight:700 }}>AED {Number(rev).toFixed(2)}</td>
                  <td style={{ padding:'11px 14px', color:'var(--text2)' }}>
                    {stats.totalRevenue > 0 ? `${((rev / stats.totalRevenue) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Accounts Tab ─────────────────────────────────────────────
function AccountsTab({ accounts, onRefresh, resetTarget, setResetTarget, resetForm, setResetForm, resetMsg, setResetMsg, showMsg }) {
  const [saving, setSaving] = React.useState(false);
  const [confirm, setConfirm] = React.useState(null); // { message, onConfirm } for ConfirmModal

  if (!accounts) return (
    <div className="card" style={{ textAlign:'center', padding:60, color:'var(--text3)' }}>
      <div style={{ fontSize:32, marginBottom:12 }}>👤</div>
      <div style={{ marginBottom:16 }}>No accounts loaded</div>
      <button className="btn btn-primary" onClick={onRefresh}>Load Accounts</button>
    </div>
  );

  const handleReset = async () => {
    if (!resetForm.newEmail && !resetForm.newPassword) {
      return setResetMsg('❌ Enter a new email or password.');
    }
    setSaving(true); setResetMsg('');
    try {
      const { default: api } = await import('../services/api');
      await api.post('/super-admin/reset-account', {
        accountId:   resetTarget.id,
        accountType: resetTarget.type,
        newEmail:    resetForm.newEmail  || undefined,
        newPassword: resetForm.newPassword || undefined,
      });
      setResetMsg('✅ Account updated.');
      setResetForm({ newEmail:'', newPassword:'' });
      onRefresh();
    } catch (err) {
      setResetMsg('❌ ' + (err.response?.data?.error || 'Failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendResetLink = async () => {
    try {
      setResetMsg('Sending...');
      await api.post('/super-admin/send-reset-link', {
        accountId:   resetTarget.id,
        accountType: resetTarget.type,
      });
      setResetMsg('✅ Reset link sent to ' + resetTarget.email);
      setTimeout(() => { setResetTarget(null); setResetMsg(''); }, 3000);
    } catch (err) {
      setResetMsg('❌ ' + (err.response?.data?.error || 'Failed to send'));
    }
  };

  const vendorAction = async (row, request, okMsg) => {
    try { await request(); showMsg(`✅ ${row.name} ${okMsg}`); onRefresh(); }
    catch (err) { showMsg('❌ ' + (err.response?.data?.error || 'Action failed')); }
  };
  const askDeactivate = (row) => setConfirm({
    message: `Deactivate ${row.name}? They will be blocked from all operations.`, confirmLabel: 'Deactivate',
    onConfirm: () => vendorAction(row, () => api.post(`/super-admin/vendors/${row.id}/deactivate`), 'deactivated'),
  });
  const askReactivate = (row) => setConfirm({
    message: `Reactivate ${row.name}? They will be able to log in again. Mall links must be re-enabled by the mall admin.`, confirmLabel: 'Reactivate', danger: false,
    onConfirm: () => vendorAction(row, () => api.post(`/super-admin/vendors/${row.id}/reactivate`), 'reactivated'),
  });
  const askDelete = (row) => setConfirm({
    message: `Permanently delete ${row.name}? This cannot be undone.`, confirmLabel: 'Delete',
    onConfirm: () => vendorAction(row, () => api.delete(`/super-admin/vendors/${row.id}`), 'deleted'),
  });

  const VENDOR_BADGE = {
    INVITED:['Invited','badge-amber'], PENDING_KYC:['Pending KYC','badge-blue'], UNDER_REVIEW:['Under Review','badge-blue'],
    ACTIVE:['Active','badge-green'], SUSPENDED:['Suspended','badge-red'], INACTIVE:['Inactive','badge-grey'],
  };

  const allRows = [
    ...(accounts.vendors    || []).map(v => ({ ...v, type:'vendor',    badge:'Vendor',    color:'var(--accent)' })),
    ...(accounts.mallAdmins || []).map(a => ({ ...a, type:'malladmin', badge:'Mall Admin', color:'#7F77DD' })),
  ].sort((a,b) => a.name?.localeCompare(b.name));

  return (
    <div>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          {(accounts.vendors||[]).length} vendors · {(accounts.mallAdmins||[]).length} mall admins
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onRefresh}>↻ Refresh</button>
      </div>

      {/* Reset form panel */}
      {resetTarget && (
        <div className="card" style={{ marginBottom:20, border:'1px solid var(--accent)', background:'var(--bg3)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <div style={{ fontWeight:700 }}>Reset credentials — {resetTarget.name}</div>
              <div style={{ fontSize:12, color:'var(--text2)' }}>{resetTarget.email} · <span style={{ color:'var(--accent)' }}>{resetTarget.badge}</span></div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => { setResetTarget(null); setResetForm({ newEmail:'', newPassword:'' }); setResetMsg(''); }}>✕ Cancel</button>
          </div>
          <button className="btn btn-ghost" style={{ width: '100%', marginBottom: 12 }}
            onClick={handleSendResetLink}>
            📧 Send Password Reset Link to {resetTarget.email}
          </button>
          <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, marginBottom: 12 }}>— or set a temporary password manually —</div>
          <div className="section-label" style={{ marginBottom:8 }}>Set Temporary Password</div>
          <div className="grid2" style={{ gap:12, marginBottom:14 }}>
            <div>
              <div className="section-label" style={{ marginBottom:6 }}>New email (optional)</div>
              <input type="email" value={resetForm.newEmail} onChange={e => setResetForm(f => ({ ...f, newEmail: e.target.value }))} placeholder={resetTarget.email} />
            </div>
            <div>
              <div className="section-label" style={{ marginBottom:6 }}>New password (optional)</div>
              <input type="password" value={resetForm.newPassword} onChange={e => setResetForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Min 8 characters" />
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>
                ⚠️ Vendor/Mall admin will be required to change this password on next login
              </div>
            </div>
          </div>
          {resetMsg && (
            <div style={{ fontSize:13, marginBottom:12, color: resetMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{resetMsg}</div>
          )}
          <button className="btn btn-primary" onClick={handleReset} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
        </div>
      )}

      {/* Accounts table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>{['Name','Email','Type','Status','Actions'].map(h => (
              <th key={h} style={{ textAlign:'left', padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {allRows.map(row => (
              <tr key={row.id} style={{ opacity: row.isActive === false ? 0.5 : 1 }}>
                <td style={{ padding:'11px 14px', fontWeight:600 }}>{row.name}</td>
                <td style={{ padding:'11px 14px', color:'var(--text2)', fontFamily:'monospace', fontSize:12 }}>{row.email}</td>
                <td style={{ padding:'11px 14px' }}>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'var(--bg4)', color: row.color }}>{row.badge}</span>
                </td>
                <td style={{ padding:'11px 14px' }}>
                  {row.type === 'vendor' && row.status ? (() => {
                    const [label, cls] = VENDOR_BADGE[row.status] || [row.status, 'badge-grey'];
                    return <span className={`badge ${cls}`}>{label}</span>;
                  })() : (
                    <span className={`badge ${row.isActive !== false ? 'badge-green' : 'badge-red'}`}>
                      {row.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td style={{ padding:'11px 14px' }}>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => { setResetTarget({ id: row.id, type: row.type, name: row.name, email: row.email, badge: row.badge }); setResetForm({ newEmail:'', newPassword:'' }); setResetMsg(''); }}
                    style={{ fontSize:12 }}>
                    🔑 Reset
                  </button>
                  {row.type === 'vendor' && ['ACTIVE','UNDER_REVIEW','SUSPENDED'].includes(row.status) && (
                    <button className="btn btn-danger btn-sm" onClick={() => askDeactivate(row)} style={{ fontSize:12 }}>Deactivate</button>
                  )}
                  {row.type === 'vendor' && row.status === 'INACTIVE' && (
                    <button className="btn btn-primary btn-sm" onClick={() => askReactivate(row)} style={{ fontSize:12 }}>Reactivate</button>
                  )}
                  {row.type === 'vendor' && ['INVITED','PENDING_KYC'].includes(row.status) && (
                    <button className="btn btn-danger btn-sm" onClick={() => askDelete(row)} style={{ fontSize:12 }}>Delete</button>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


